export const TOPCLIP_AUTO_PROGRESS_STAGES = [
  "Starting your job",
  "Importing your video",
  "Finding the strongest moments",
  "Creating your clips",
  "Saving your clips",
] as const;

export interface TopClipAutoJobTiming {
  acceptedAtMs: number;
  estimatedProcessingMs: number;
}

const STORAGE_PREFIX = "topclip:auto-job-timing:v1";
const ESTIMATED_PROCESSING_RATIO = 0.4;
const ESTIMATED_PROCESSING_STAGE_COUNT = 4;
const LAST_ACTIVE_STAGE_INDEX = TOPCLIP_AUTO_PROGRESS_STAGES.length - 1;

const timingKey = (ownerId: string, jobId: string): string =>
  `${STORAGE_PREFIX}:${encodeURIComponent(ownerId)}:${encodeURIComponent(jobId)}`;

const isUsableIdentifier = (value: string): boolean => value.trim() !== "";

const isValidTiming = (value: unknown): value is TopClipAutoJobTiming => {
  if (!value || typeof value !== "object") return false;
  const timing = value as Record<string, unknown>;
  return (
    typeof timing.acceptedAtMs === "number" &&
    Number.isFinite(timing.acceptedAtMs) &&
    timing.acceptedAtMs > 0 &&
    typeof timing.estimatedProcessingMs === "number" &&
    Number.isFinite(timing.estimatedProcessingMs) &&
    timing.estimatedProcessingMs > 0
  );
};

export const createTopClipAutoJobTiming = (
  sourceDurationSeconds: number | null,
  acceptedAtMs: number = Date.now()
): TopClipAutoJobTiming | null => {
  if (
    sourceDurationSeconds === null ||
    !Number.isFinite(sourceDurationSeconds) ||
    sourceDurationSeconds <= 0 ||
    !Number.isFinite(acceptedAtMs) ||
    acceptedAtMs <= 0
  ) {
    return null;
  }

  return {
    acceptedAtMs,
    estimatedProcessingMs:
      sourceDurationSeconds * ESTIMATED_PROCESSING_RATIO * 1_000,
  };
};

export const saveTopClipAutoJobTiming = (
  ownerId: string,
  jobId: string,
  sourceDurationSeconds: number | null,
  acceptedAtMs: number = Date.now()
): TopClipAutoJobTiming | null => {
  if (!isUsableIdentifier(ownerId) || !isUsableIdentifier(jobId)) return null;
  const timing = createTopClipAutoJobTiming(
    sourceDurationSeconds,
    acceptedAtMs
  );
  if (!timing) return null;

  try {
    window.localStorage.setItem(
      timingKey(ownerId, jobId),
      JSON.stringify(timing)
    );
    return timing;
  } catch {
    return null;
  }
};

export const readTopClipAutoJobTiming = (
  ownerId: string,
  jobId: string
): TopClipAutoJobTiming | null => {
  if (!isUsableIdentifier(ownerId) || !isUsableIdentifier(jobId)) return null;

  try {
    const key = timingKey(ownerId, jobId);
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    const timing: unknown = JSON.parse(raw);
    if (isValidTiming(timing)) return timing;
    window.localStorage.removeItem(key);
  } catch {
    // A local estimate is optional and never authoritative.
  }
  return null;
};

export const clearTopClipAutoJobTiming = (
  ownerId: string,
  jobId: string
): void => {
  if (!isUsableIdentifier(ownerId) || !isUsableIdentifier(jobId)) return;
  try {
    window.localStorage.removeItem(timingKey(ownerId, jobId));
  } catch {
    // A local estimate is optional and never authoritative.
  }
};

export const estimatedTopClipAutoStageIndex = (
  timing: TopClipAutoJobTiming,
  nowMs: number = Date.now()
): number => {
  const elapsedMs = Math.max(0, nowMs - timing.acceptedAtMs);
  const stageDurationMs =
    timing.estimatedProcessingMs / ESTIMATED_PROCESSING_STAGE_COUNT;
  return Math.min(
    LAST_ACTIVE_STAGE_INDEX,
    1 + Math.floor(elapsedMs / stageDurationMs)
  );
};

export const estimatedTopClipAutoProgressPercent = (
  timing: TopClipAutoJobTiming,
  nowMs: number = Date.now()
): number => {
  const elapsedMs = Math.max(0, nowMs - timing.acceptedAtMs);
  const elapsedRatio = Math.min(1, elapsedMs / timing.estimatedProcessingMs);
  return 20 + elapsedRatio * 75;
};
